import { JsonSchemaEnum } from '@/business-components/json-schema-enum/JsonSchemaEnum';
import { MarkdownViewer } from '@/business-components/markdown-viewer/MarkdownViewer';
import { useMeasure } from '@/hooks/useMeasure';
import usePersistFn from '@/hooks/usePersistFn';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import type { IHttpApiParameterAndIsCommon } from '@/services/http-api/httpApi';
import type { IWebSocketApiParameterAndIsCommon } from '@/services/websocket-api/webSocketApi';
import { parameterConvertToSchema } from '@/shared/util/parameterConvertToSchema';
import { JsonSchemaViewer } from '@/ui-kit/components/JsonSchemaViewer/JsonSchemaViewer';
import { JsonSchemaViewerCopyableName } from '@/ui-kit/components/JsonSchemaViewer/components/PropertyNameRender';
import { AsyncSchemaGeneratorModal } from '@/ui-kit/components/SchemaGenerator';
import type { ViewMode } from '@tarslib/json-schema-viewer';
import { ParametersLocationType } from '@tarslib/types/lib/httpApi';
import { ParameterDisplayMode } from '@tarslib/types/lib/projectSetting';
import { AppJsonSchema2JsonSchemaStandard } from '@tarslib/utils/lib/json-schema/AppJsonSchema2JsonSchemaStandard';
import type { JSONSchema } from '@tarslib/utils/lib/json-schema/type';
import { Button, Card } from 'aui';
import { FoxIconCode02 } from 'aui-biz-icons';
import clsx from 'clsx';
import type { JSONSchema7 } from 'json-schema';
import cloneDeep from 'lodash-es/cloneDeep';
import React from 'react';
import styles from './ParametersCard.module.css';

type ParametersType =
  | ParametersLocationType.Path
  | ParametersLocationType.Query
  | ParametersLocationType.Header
  | ParametersLocationType.Cookie
  | ParametersLocationType.Body;

export type ParametersCardProps = {
  type: ParametersType;
  value:
    | IWebSocketApiParameterAndIsCommon[]
    | IHttpApiParameterAndIsCommon[]
    | JSONSchema
    | JSONSchema7;
  mode?: ParameterDisplayMode;
  title?: React.ReactNode;
  extraNode?: React.ReactNode;
  cardBodyRender?: (originalNode: React.ReactNode) => React.ReactNode;
  viewMode: ViewMode;
  hiddenExtra?: boolean;
  bordered?: boolean;
  className?: string;
  generatorModalOpen?: boolean;
  onGeneratorModalOpenChange?: (open: boolean) => void;
};

export function ParametersCard(props: ParametersCardProps) {
  const {
    type,
    value,
    mode,
    title,
    className,
    extraNode,
    bordered = true,
    hiddenExtra,
    viewMode,
    cardBodyRender = (v) => v,
    generatorModalOpen = false,
    onGeneratorModalOpenChange,
  } = props;
  const advancedSettings = useDocsDataModelStore((x) => x.projectSetting.advancedSettings);
  const parametersDisplayMode = advancedSettings?.parameterDisplayMode;
  const [containerRef, isModernMode] = useMeasure(
    ({ width: containerWidth }) => {
      return containerWidth < 640 ? true : false;
    },
    Object.is,
    true,
  );
  const displayMode = React.useMemo(() => {
    if (mode === ParameterDisplayMode.MODERN) {
      return ParameterDisplayMode.MODERN;
    }

    if (
      mode === ParameterDisplayMode.CLASSIC ||
      (parametersDisplayMode === ParameterDisplayMode.CLASSIC && !isModernMode)
    ) {
      return ParameterDisplayMode.CLASSIC;
    } else {
      return ParameterDisplayMode.MODERN;
    }
  }, [isModernMode, mode, parametersDisplayMode]);

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const newTitle = React.useMemo(() => {
    if (title) {
      return title;
    }

    if (type === ParametersLocationType.Path) {
      return clientI18n('message.pathParameter');
    } else if (type === ParametersLocationType.Query) {
      return clientI18n('message.queryParameter');
    } else if (type === ParametersLocationType.Header) {
      return clientI18n('message.headerParameter');
    } else if (type === ParametersLocationType.Cookie) {
      return clientI18n('message.cookieParameter');
    } else if (type === ParametersLocationType.Body) {
      return clientI18n('message.bodyParameter');
    }
    return undefined;
  }, [title, type, clientI18n]);

  const dataSchemaDefinitionMap = useDocsDataModelStore(
    (x) => x.resourceData.extraData.dataSchemaDefinitions,
  );
  const onlineShareTheme = useAppearanceConfigModelStore((x) => x.appTheme);
  const locale = useDocsDataModelStore((x) => x.docsBaseConfig.language);
  const appNameLower = useEnvConfigModelStore((x) => x.APP_NAME_LOWER);
  const appName = useEnvConfigModelStore((x) => x.APP_NAME);
  const schema = React.useMemo(() => {
    if (!Array.isArray(value)) {
      const newSchema = cloneDeep(value);
      if (typeof newSchema === 'object') {
        newSchema.definitions = {
          ...dataSchemaDefinitionMap,
          ...newSchema.definitions,
        };
      }

      return AppJsonSchema2JsonSchemaStandard(newSchema, [], {
        enableXAppEnum: true,
        enableXAppMock: true,
      });
    }

    return parameterConvertToSchema(value, dataSchemaDefinitionMap, appNameLower) || {};
  }, [appNameLower, dataSchemaDefinitionMap, value]);
  const [visible, setVisible] = React.useState(generatorModalOpen);
  React.useEffect(() => {
    setVisible(generatorModalOpen);
  }, [generatorModalOpen]);

  const newExtraNode = React.useMemo(() => {
    if (hiddenExtra) {
      return null;
    }

    return (
      <>
        <Button
          type="soft"
          theme="secondary"
          icon={<FoxIconCode02 />}
          onClick={(e) => {
            setVisible(true);
            onGeneratorModalOpenChange?.(true);
            e.stopPropagation();
          }}
          size="small"
        >
          {clientI18n('codeGenerate')}
        </Button>

        {extraNode}
      </>
    );
  }, [clientI18n, hiddenExtra, extraNode, onGeneratorModalOpenChange]);

  const descriptionRender = usePersistFn((description: string) => (
    <MarkdownViewer value={description} enableHeaderPlugin={false} inDocView={false} />
  ));

  const expandedInfos = React.useMemo(() => {
    return {
      typesRightRender: () => <></>,
    };
  }, []);
  const valueRenderMap = React.useMemo(() => {
    return {
      enum: (name: string, enumDescriptions?: JSONSchema['x-app-enum']) => {
        return <JsonSchemaEnum name={name} enumDescriptions={enumDescriptions} />;
      },
    };
  }, []);

  return (
    <Card
      size="small"
      title={newTitle}
      className={clsx(
        styles['parameters-card'],
        styles['parameters-card-body'],
        {
          [`${styles['parameters-classic']}`]: displayMode === ParameterDisplayMode.CLASSIC,
          [`${styles['parameters-card--no-border']}`]: !bordered,
          [`${styles['parameters-card-body--no-border']}`]: !bordered,
        },
        className,
      )}
      extra={newExtraNode}
      bordered={bordered}
      ref={containerRef}
    >
      {cardBodyRender(
        <JsonSchemaViewer
          schema={schema}
          locale={locale}
          disableCrumbs
          propertyNameRender={JsonSchemaViewerCopyableName}
          isStateRightAligned={false}
          descriptionRender={descriptionRender}
          expandedInfos={expandedInfos}
          viewMode={viewMode}
          isMergeAllOf
          displayMode={displayMode as any}
          appNameLower={appNameLower}
          valueRenderMap={valueRenderMap}
        />,
      )}

      {visible && (
        <AsyncSchemaGeneratorModal
          visible={visible}
          onCancel={() => {
            setVisible(false);
            onGeneratorModalOpenChange?.(false);
          }}
          schema={schema}
          schemaName={schema?.title || `${appName} Model`}
          theme={onlineShareTheme}
        />
      )}
    </Card>
  );
}
